import { makeAutoObservable, runInAction } from "mobx";

import {
  checkoutSubscription,
  getApiErrorMessage,
  getCurrentSubscription,
  listAvailableSubscriptionPlans,
  openTeamBillingPortal,
} from "@/api";
import { getBillingReturnPath } from "@/utils/getBillingReturnPath";
import { redirectTo } from "@/utils/redirectTo";

import type { Subscription, SubscriptionPlan } from "@/api";
import type { TeamModel } from "@/models/TeamModel";
import type { RootStore } from "@/store/RootStore";

export class UpgradePlanDialogStore {
  rootStore: RootStore;

  isOpen = false;

  /** Team being upgraded */
  team: TeamModel | undefined = undefined;

  status: "loading" | "ready" | "error" = "loading";

  plans: SubscriptionPlan[] = [];

  subscription: Subscription | null = null;

  error: string | null = null;

  checkingOutPlanUid: string | null = null;

  isDowngrading = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  open(team: TeamModel) {
    this.team = team;
    this.status = "loading";
    this.plans = [];
    this.subscription = null;
    this.error = null;
    this.checkingOutPlanUid = null;
    this.isDowngrading = false;
    this.isOpen = true;
  }

  get isPersonalTeam() {
    return this.team?.personal ?? false;
  }

  get availablePlan() {
    const kind: SubscriptionPlan["kind"] = this.isPersonalTeam ? "personal" : "team";
    return this.plans.find((plan) => plan.kind === kind);
  }

  get subscribedPlan() {
    return this.subscription?.subscriptionPlan ?? null;
  }

  get isSubscribed() {
    return this.subscribedPlan !== null;
  }

  get isCanceled() {
    return Boolean(this.subscription?.endsAt);
  }

  close() {
    this.isOpen = false;
  }

  async load() {
    if (!this.isOpen || !this.rootStore.auth.isLoggedIn) return;

    const { team } = this;
    this.status = "loading";
    this.error = null;

    try {
      const [plansResult, subscriptionResult] = await Promise.all([
        listAvailableSubscriptionPlans(),
        getCurrentSubscription({ teamUid: team?.uid }),
      ]);
      if (this.team !== team) return;
      runInAction(() => {
        this.plans = plansResult.subscriptionPlans ?? [];
        this.subscription = subscriptionResult.subscription ?? null;
        this.status = "ready";
      });
    } catch (err) {
      if (this.team !== team) return;
      runInAction(() => {
        this.error = getApiErrorMessage(err);
        this.status = "error";
      });
    }
  }

  async checkout(plan: SubscriptionPlan) {
    const { team } = this;
    if (!team) return;

    this.checkingOutPlanUid = plan.uid;
    this.error = null;

    const result = await redirectTo(() =>
      checkoutSubscription({
        subscriptionPlanUid: plan.uid,
        teamUid: team.uid,
        returnPath: getBillingReturnPath(team),
      }),
    );
    if (result.isRedirecting) return;

    runInAction(() => {
      this.error = result.error;
      this.checkingOutPlanUid = null;
    });
  }

  async downgrade() {
    const { team } = this;
    if (!team) return;

    this.isDowngrading = true;
    this.error = null;

    const result = await redirectTo(() =>
      openTeamBillingPortal(team.permalink, { returnPath: getBillingReturnPath(team) }),
    );
    if (result.isRedirecting) return;

    runInAction(() => {
      this.error = result.error;
      this.isDowngrading = false;
    });
  }
}
